import { useState, useEffect, useRef, Suspense, useCallback } from 'react'
import {
  Stack,
  Text,
  Group,
  Paper,
  Title,
  Alert,
  Loader,
  Center,
} from '@mantine/core'
import styles from './DeviceDashboard.module.css'
import {
  requestDeviceCode,
  checkDeviceStatus,
  getDashboardData,
  getCurrentDashboard,
  getStoredDeviceToken,
  getStoredDevicePlatformId,
  saveDeviceToken,
  clearDeviceToken,
  activateDeviceSession,
  isConnectionError,
  isAuthenticationError,
  DeviceLinkRequest,
  DeviceLinkStatus,
  DashboardData,
  DashboardInfo,
  ApiError,
  useDetectPlatformOptional,
} from './api'
import QRCode from 'qrcode'
import { LeaguestarState } from '../state'
import { Dashboard } from '../dashboard/Dashboard'
import { useActiveCompetitions } from '../navigation/api'
import { init } from '@noriginmedia/norigin-spatial-navigation'
import { ErrorBoundary, FallbackProps } from 'react-error-boundary'
import { handleError } from '../common/error-handler'
import {
  detectTVPlatform,
  isTVPlatform,
  isTVBackKey,
  exitTVApp,
  forwardTVKeyToShell,
  notifyShellReady,
  startShellHeartbeat,
} from '../common/tv-platform'

// How often to retry reaching the API while the TV is offline.
const RETRY_INTERVAL_MS = 10000

// Ceiling for the backed-off retry after a render error.
const MAX_RETRY_INTERVAL_MS = 120000

export type RoarNotification = {
  post_id: number
  text: string
  author_name: string
  author_avatar: string | null
}

// Wrapper component that sets up competition context and renders Dashboard
function DashboardWithCompetition({
  dashboardCompetitionId,
  roarNotification,
  onRoarDone,
}: {
  dashboardCompetitionId?: number
  roarNotification?: RoarNotification | null
  onRoarDone?: () => void
}) {
  const { data } = useActiveCompetitions()
  const competitions = data?.competitions || []
  const competitionId = LeaguestarState.useState(
    (state) => state.competition_id
  )

  useEffect(() => {
    // If dashboard has a specific competition_id, use it
    if (dashboardCompetitionId) {
      LeaguestarState.update((state) => {
        state.competition_id = dashboardCompetitionId
      })
    } else if (
      competitions &&
      competitions.length > 0 &&
      (!competitionId || competitionId === 0)
    ) {
      // Otherwise fall back to first active competition
      LeaguestarState.update((state) => {
        state.competition_id = competitions[0].competition_id
      })
    }
  }, [competitions, competitionId, dashboardCompetitionId])

  // If dashboard specifies a competition, we don't need to check for active competitions
  if (!dashboardCompetitionId && (!competitions || competitions.length === 0)) {
    return (
      <Center h="100%">
        <Text c="dimmed">No active competitions found</Text>
      </Center>
    )
  }

  // Wait until competition_id is set in state before rendering Dashboard
  if (!competitionId || competitionId === 0) {
    return (
      <Center h="100%">
        <Loader size="xl" color="cyan" />
      </Center>
    )
  }

  return (
    <Dashboard
      roarNotification={roarNotification}
      onRoarDone={onRoarDone}
      useDeviceConfig
    />
  )
}

function DeviceDashboardWithPlatform() {
  const { data: platformData } = useDetectPlatformOptional()

  useEffect(() => {
    if (platformData?.platform_id) {
      LeaguestarState.update((state) => {
        state.platforms = [
          { platform_id: platformData.platform_id!, platform_name: '' },
        ]
        state.selected_platform_id = platformData.platform_id!
      })
    }
  }, [platformData?.platform_id])

  return <DeviceDashboardInner />
}

/**
 * Full-screen message used for every unrecoverable-looking state on a TV.
 * Everything here is plain text so the Samsung Voice Guide reads it out.
 */
function TVMessageScreen({
  title,
  message,
  hint,
}: {
  title: string
  message: string
  hint?: string
}) {
  return (
    <div className={styles.page}>
      <div className={styles.container}>
        <Stack
          align="center"
          gap="lg"
          className={styles.networkError}
          role="alert"
          aria-live="assertive"
        >
          <Title order={2} c="white" ta="center">
            {title}
          </Title>
          <Text size="xl" c="gray.4" ta="center">
            {message}
          </Text>
          <Group gap="sm">
            <Loader size="sm" color="cyan" />
            <Text size="lg" c="gray.5">
              Reconnecting...
            </Text>
          </Group>
          <Text size="md" c="gray.6">
            {hint ?? 'Press Return or Exit on the remote to close the app.'}
          </Text>
        </Stack>
      </div>
    </div>
  )
}

/**
 * TV-local error boundary. Without it any failed query on this route falls
 * through to the app-wide fallback, which renders "Authentication Error" - a
 * dead end on a TV, and a Samsung certification failure.
 *
 * Retries are one-shot and backed off, never a fixed interval: a deterministic
 * error retried every 10s renders as the dashboard and the error screen
 * flapping back and forth, which looks far worse than a stable message.
 */
function TVErrorFallback({ error, resetErrorBoundary }: FallbackProps) {
  const connectionProblem = isConnectionError(error)
  const attemptRef = useRef(0)

  useEffect(() => {
    // The boundary swallows the error otherwise, which makes this
    // undiagnosable from the TV's console.
    console.error('[tv] render failed:', error)
  }, [error])

  useEffect(() => {
    attemptRef.current += 1
    const delay = Math.min(
      RETRY_INTERVAL_MS * Math.pow(2, attemptRef.current - 1),
      MAX_RETRY_INTERVAL_MS
    )

    const retry = () => resetErrorBoundary()
    const timeout = setTimeout(retry, delay)
    window.addEventListener('online', retry)

    return () => {
      clearTimeout(timeout)
      window.removeEventListener('online', retry)
    }
  }, [error, resetErrorBoundary])

  if (connectionProblem) {
    return (
      <TVMessageScreen
        title="Network is not connected"
        message="Leaguestar cannot reach the internet. Check the network settings on your TV - the dashboard resumes automatically once the connection is back."
      />
    )
  }

  // Not a network problem - say so rather than blaming the network, which is
  // both misleading to a reviewer and a review defect in its own right.
  return (
    <TVMessageScreen
      title="Unable to load the dashboard"
      message="Leaguestar could not load your dashboard. It will keep trying automatically."
      hint="Press Return or Exit on the remote to close the app."
    />
  )
}

export default function DeviceDashboard() {
  // The TV shell keeps its own splash up until the page reports in. Do this as
  // early as possible so a slow dashboard load is not mistaken for a dead app.
  useEffect(() => {
    notifyShellReady()
    return startShellHeartbeat()
  }, [])

  // Remote Return/Exit handling. Samsung and LG both require these keys to
  // close the app when there is no previous screen, and /tv is a single screen
  // with no internal navigation - so Return always means "leave the app".
  // Inside a packaged shell the shell keeps focus and handles the keys itself;
  // this is the fallback for when the frame is focused anyway. Registered here,
  // outside the error boundary, so the remote still works on the error screen.
  useEffect(() => {
    const handleKeyDown = (e: KeyboardEvent) => {
      if (!isTVBackKey(e)) return
      e.preventDefault()
      // The packaged Tizen/webOS shell owns the Return key policy, so give it
      // the key rather than exiting behind its back.
      if (forwardTVKeyToShell(e)) return
      exitTVApp()
    }

    window.addEventListener('keydown', handleKeyDown)
    return () => window.removeEventListener('keydown', handleKeyDown)
  }, [])

  return (
    <ErrorBoundary FallbackComponent={TVErrorFallback} onError={handleError}>
      <Suspense
        fallback={
          <div className={styles.page}>
            <div className={styles.container}>
              <Center className={styles.loadingContainer}>
                <Stack align="center" gap="md">
                  <Loader size="xl" color="cyan" />
                </Stack>
              </Center>
            </div>
          </div>
        }
      >
        <DeviceDashboardWithPlatform />
      </Suspense>
    </ErrorBoundary>
  )
}

function DeviceDashboardInner() {
  const [deviceCode, setDeviceCode] = useState<DeviceLinkRequest | null>(null)
  const [isRequesting, setIsRequesting] = useState(false)
  const [status, setStatus] = useState<DeviceLinkStatus | null>(null)
  const [isPolling, setIsPolling] = useState(false)
  const [error, setError] = useState<string | null>(null)
  const [qrDataUrl, setQrDataUrl] = useState<string>('')
  const [dashboardData, setDashboardData] = useState<DashboardData | null>(null)
  const [currentDashboard, setCurrentDashboard] =
    useState<DashboardInfo | null>(null)
  const [isLoadingDashboard, setIsLoadingDashboard] = useState(false)
  const [pollInterval, setPollInterval] = useState<NodeJS.Timeout | null>(null)
  const isApprovedRef = useRef(false)
  const activeCodeRef = useRef<string | null>(null)
  const [tvNotification, setTvNotification] = useState<RoarNotification | null>(
    null
  )
  const notificationTimeoutRef = useRef<NodeJS.Timeout | null>(null)
  const wsRef = useRef<WebSocket | null>(null)
  const [connectionLost, setConnectionLost] = useState(false)
  const bootstrapRef = useRef<() => void>(() => {})

  // Initialize spatial navigation for TV platforms
  useEffect(() => {
    if (isTVPlatform()) {
      init({
        debug: false,
        visualDebug: false,
      })
    }
  }, [])

  // Load the dashboard with a stored token, or start the linking flow.
  // Also used as the retry entry point when the TV regains network.
  const bootstrap = async () => {
    if (dashboardData) return

    setError(null)
    const storedToken = getStoredDeviceToken()

    if (!storedToken) {
      handleRequestCode()
      return
    }

    setIsLoadingDashboard(true)
    try {
      activateDeviceSession(
        storedToken,
        getStoredDevicePlatformId() ?? undefined
      )
      const data = await getDashboardData(storedToken)
      setConnectionLost(false)
      setDashboardData(data)
      // Set fake status to show approval state
      setStatus({ is_approved: true, user_name: data.user_name })
      // Also fetch current dashboard info
      try {
        const dashboard = await getCurrentDashboard(storedToken)
        setCurrentDashboard(dashboard)
      } catch {
        // Dashboard info fetch failed, but that's okay
      }
    } catch (err: unknown) {
      if (isAuthenticationError(err)) {
        // The link was revoked or the token expired - re-link.
        clearDeviceToken()
        handleRequestCode()
      } else {
        // Network down, DNS failure or server error. Keep the link and retry;
        // dropping the token here made a brief outage look like a logout.
        console.error('[tv] dashboard load failed, going offline:', err)
        setConnectionLost(true)
      }
    } finally {
      setIsLoadingDashboard(false)
    }
  }

  // Keep the retry entry point pointing at the latest closure. Declared before
  // the mount effect so it is populated by the time that runs.
  useEffect(() => {
    bootstrapRef.current = () => {
      bootstrap()
    }
  })

  useEffect(() => {
    bootstrapRef.current()
  }, [])

  // Retry while the TV cannot reach the API, so the app resumes on its own
  // once the network comes back. `online` covers a cable/Wi-Fi reconnect; the
  // interval covers cases where the link is up but the route is blocked.
  useEffect(() => {
    if (!connectionLost) return

    const retry = () => bootstrapRef.current()
    const interval = setInterval(retry, RETRY_INTERVAL_MS)
    window.addEventListener('online', retry)

    return () => {
      clearInterval(interval)
      window.removeEventListener('online', retry)
    }
  }, [connectionLost])

  // Auto-call test dashboard when approved
  useEffect(() => {
    if (status?.is_approved && deviceCode && !dashboardData) {
      handleTestDashboard()
    }
  }, [status?.is_approved, deviceCode, dashboardData])

  const handleRequestCode = async () => {
    // Don't request new code if dashboard data is already loaded
    if (dashboardData) return

    // Don't request new code if we already have one and it's not expired
    if (deviceCode && new Date(deviceCode.expires_at) > new Date()) {
      return
    }

    setIsRequesting(true)
    setError(null)
    setStatus(null)
    setQrDataUrl('')
    setDashboardData(null)

    try {
      const code = await requestDeviceCode(detectTVPlatform())
      setConnectionLost(false)
      setDeviceCode(code)

      // Generate QR code
      const approvalUrl = `${window.location.origin}/device?code=${code.code}`
      await generateQRCode(approvalUrl)

      // Start polling automatically
      startPolling(code)
    } catch (err: unknown) {
      if (isConnectionError(err)) {
        // Show the network guide instead of an auth/API error - the TV is
        // simply offline and the retry loop will pick it up.
        console.error('[tv] device code request failed, going offline:', err)
        setConnectionLost(true)
      } else {
        const apiError = err as ApiError
        setError(
          apiError.response?.data?.error ||
            apiError.message ||
            'Failed to request device code'
        )
      }
    } finally {
      setIsRequesting(false)
    }
  }

  const generateQRCode = async (url: string) => {
    try {
      const qrDataURL = await QRCode.toDataURL(url, {
        width: 500,
        margin: 2,
        color: {
          dark: '#000000',
          light: '#ffffff',
        },
      })
      setQrDataUrl(qrDataURL)
    } catch (err) {
      console.error('Failed to generate QR code:', err)
      setError('Failed to generate QR code')
    }
  }

  const stopPolling = () => {
    if (pollInterval) {
      clearInterval(pollInterval)
      setPollInterval(null)
    }
    setIsPolling(false)
    activeCodeRef.current = null
  }

  const startPolling = (code: DeviceLinkRequest) => {
    // ALWAYS stop any existing polling first
    stopPolling()

    // Don't start polling if already approved
    if (isApprovedRef.current) {
      return
    }

    // Check if we're trying to poll the same code
    if (activeCodeRef.current === code.code) {
      return
    }

    activeCodeRef.current = code.code
    setIsPolling(true)

    const interval = setInterval(async () => {
      // Stop polling immediately if already approved
      if (isApprovedRef.current) {
        clearInterval(interval)
        setPollInterval(null)
        setIsPolling(false)
        activeCodeRef.current = null
        return
      }

      // Make sure we're still polling the right code
      if (activeCodeRef.current !== code.code) {
        clearInterval(interval)
        return
      }

      try {
        const currentStatus = await checkDeviceStatus(code.code)

        // Update status only if this is still the active code
        if (activeCodeRef.current === code.code) {
          setStatus(currentStatus)
        }

        if (currentStatus.is_approved) {
          isApprovedRef.current = true
          clearInterval(interval)
          setPollInterval(null)
          setIsPolling(false)
          activeCodeRef.current = null
        }
      } catch (err: unknown) {
        // A blip while waiting for approval is not fatal - keep polling.
        if (isConnectionError(err)) {
          return
        }
        // Stop polling on critical errors but DON'T request new code
        const apiError = err as ApiError
        if (apiError.response?.status === 404) {
          clearInterval(interval)
          setPollInterval(null)
          setIsPolling(false)
          activeCodeRef.current = null
          setError('Device code expired or not found')
        }
      }
    }, code.poll_interval_seconds * 1000)

    setPollInterval(interval)
  }

  const handleTestDashboard = async () => {
    if (!deviceCode) return

    setIsLoadingDashboard(true)
    setError(null)

    try {
      // First exchange the code for a token, then test dashboard
      const { exchangeDeviceToken } = await import('./api')
      const tokenResponse = await exchangeDeviceToken({ code: deviceCode.code })

      // Save the token and platform_id for future use
      saveDeviceToken(tokenResponse.access_token, tokenResponse.platform_id)
      activateDeviceSession(
        tokenResponse.access_token,
        tokenResponse.platform_id
      )

      const data = await getDashboardData(tokenResponse.access_token)
      setDashboardData(data)

      // Also fetch current dashboard info
      try {
        const dashboard = await getCurrentDashboard(tokenResponse.access_token)
        setCurrentDashboard(dashboard)
      } catch {
        // Dashboard info fetch failed, but that's okay
      }
    } catch (err: unknown) {
      if (isConnectionError(err)) {
        console.error('[tv] token exchange failed, going offline:', err)
        setConnectionLost(true)
      } else {
        const apiError = err as ApiError
        setError(apiError.response?.data?.error || 'Failed to access dashboard')
      }
    } finally {
      setIsLoadingDashboard(false)
    }
  }

  // Cleanup polling on unmount
  useEffect(() => {
    return () => {
      stopPolling()
    }
  }, [])

  const connectTvWebSocket = useCallback((token: string) => {
    if (wsRef.current) {
      wsRef.current.close()
    }

    const wsProtocol = window.location.protocol === 'https:' ? 'wss:' : 'ws:'
    const ws = new WebSocket(
      `${wsProtocol}//${window.location.host}/api/ws/tv?token=${encodeURIComponent(token)}`
    )
    wsRef.current = ws

    ws.onmessage = (event) => {
      try {
        const msg = JSON.parse(event.data)
        if (msg.type === 'roar') {
          setTvNotification({
            post_id: msg.post_id,
            text: msg.text,
            author_name: msg.author_name,
            author_avatar: msg.author_avatar ?? null,
          })
          // Fallback safety timeout — onRoarDone normally dismisses it earlier
          if (notificationTimeoutRef.current)
            clearTimeout(notificationTimeoutRef.current)
          notificationTimeoutRef.current = setTimeout(
            () => setTvNotification(null),
            20000
          )
        }
      } catch {
        // ignore malformed messages
      }
    }

    ws.onclose = () => {
      // Reconnect after 5s if we still have an active session
      setTimeout(() => {
        const currentToken = getStoredDeviceToken()
        if (currentToken && wsRef.current === ws) {
          connectTvWebSocket(currentToken)
        }
      }, 5000)
    }
  }, [])

  // Connect WebSocket when dashboard is active
  useEffect(() => {
    if (!dashboardData) return
    const token = getStoredDeviceToken()
    if (token) connectTvWebSocket(token)

    return () => {
      wsRef.current?.close()
      wsRef.current = null
      if (notificationTimeoutRef.current)
        clearTimeout(notificationTimeoutRef.current)
    }
  }, [dashboardData, connectTvWebSocket])

  // Stop all polling when dashboard data is successfully loaded
  useEffect(() => {
    if (dashboardData) {
      stopPolling()
      isApprovedRef.current = true
    }
  }, [dashboardData])

  // Update approval ref when status changes
  useEffect(() => {
    if (status?.is_approved) {
      isApprovedRef.current = true
      stopPolling()
    }
  }, [status?.is_approved])

  // Auto-generate new code when current one expires
  useEffect(() => {
    if (!deviceCode || dashboardData) return

    const expiresAt = new Date(deviceCode.expires_at)
    const now = new Date()
    const timeUntilExpiry = expiresAt.getTime() - now.getTime()

    // If already expired, request new code immediately
    if (timeUntilExpiry <= 0) {
      handleRequestCode()
      return
    }

    // Otherwise, set timeout to request new code when it expires
    const timeoutId = setTimeout(() => {
      handleRequestCode()
    }, timeUntilExpiry)

    return () => clearTimeout(timeoutId)
  }, [deviceCode?.expires_at, dashboardData])

  const isExpired = deviceCode && new Date(deviceCode.expires_at) < new Date()
  const showDashboardData = dashboardData && status?.is_approved

  // Get device URL for display (remove protocol for cleaner look)
  const deviceUrl = `${window.location.host}/device`

  // When a dashboard is assigned, render the Dashboard component at full page
  if (showDashboardData && currentDashboard) {
    return (
      <Suspense
        fallback={
          <div className={styles.page}>
            <div className={styles.container}>
              <Center className={styles.loadingContainer}>
                <Loader size="xl" color="cyan" />
              </Center>
            </div>
          </div>
        }
      >
        <DashboardWithCompetition
          dashboardCompetitionId={currentDashboard.competition_id}
          roarNotification={tvNotification}
          onRoarDone={() => {
            if (notificationTimeoutRef.current)
              clearTimeout(notificationTimeoutRef.current)
            setTvNotification(null)
          }}
        />
      </Suspense>
    )
  }

  // Network guide screen. Samsung certification requires a clear, recoverable
  // "no network" message here - the generic error boundary used to render
  // "Authentication Error" with no way forward.
  if (connectionLost && !showDashboardData) {
    return (
      <TVMessageScreen
        title="Network is not connected"
        message="Leaguestar cannot reach the internet. Check the network settings on your TV - the dashboard resumes automatically once the connection is back."
      />
    )
  }

  return (
    <div className={styles.page}>
      {/* Main content container - 1220px x 500px */}
      <div className={styles.container}>
        {/* Loading State */}
        {(isRequesting || isLoadingDashboard) && !showDashboardData && (
          <Center className={styles.loadingContainer}>
            <Stack align="center" gap="md">
              <Loader size="xl" color="cyan" />
              <Text c="white" size="lg">
                {isRequesting
                  ? 'Requesting authorization...'
                  : 'Loading dashboard...'}
              </Text>
            </Stack>
          </Center>
        )}

        {/* Dashboard Data - Show info card when no dashboard is assigned */}
        {showDashboardData && !currentDashboard ? (
          <Stack gap="md" className={styles.dashboardContent}>
            <Paper p="xl" radius="md" className={styles.dashboardPaper}>
              <Title order={3} mb="md" ta="center" c="white">
                Dashboard Information
              </Title>
              <Stack gap="sm">
                <Group justify="space-between">
                  <Text fw={500} c="dimmed">
                    User:
                  </Text>
                  <Text c="white">{dashboardData.user_name}</Text>
                </Group>
                <Group justify="space-between">
                  <Text fw={500} c="dimmed">
                    Platform:
                  </Text>
                  <Text c="white">{dashboardData.platform_name}</Text>
                </Group>
                {dashboardData.device_name && (
                  <Group justify="space-between">
                    <Text fw={500} c="dimmed">
                      Device:
                    </Text>
                    <Text c="white">{dashboardData.device_name}</Text>
                  </Group>
                )}
              </Stack>
            </Paper>
          </Stack>
        ) : (
          /* Authorization Flow - Show when not approved */
          deviceCode &&
          !isRequesting && (
            <Group
              gap={0}
              align="center"
              justify="center"
              className={styles.authFlow}
            >
              {/* Left side - Code display */}
              <Stack align="center" gap="lg" className={styles.leftSection}>
                <Text
                  size="lg"
                  fw={600}
                  c="gray.4"
                  style={{ letterSpacing: '0.5px' }}
                >
                  Dashboard authorization code
                </Text>

                {/* Code display box */}
                <Paper p="xl" radius="md" className={styles.codeBox}>
                  <Text
                    className={`${styles.codeText} ${isExpired ? styles.codeTextExpired : styles.codeTextDefault}`}
                  >
                    {deviceCode.code}
                  </Text>
                </Paper>

                <Stack align="center" gap="xxxs">
                  <Text size="lg" c="gray.5">
                    Enter code at:
                  </Text>
                  <Text size="lg" c="cyan" fw={700}>
                    {deviceUrl}
                  </Text>
                </Stack>

                {/* Status indicator */}
                <Paper p="md" radius="md" className={styles.statusBox}>
                  <Group justify="center" gap="sm">
                    {isPolling && !status?.is_approved && (
                      <Loader size="sm" color="cyan" />
                    )}
                    <Text
                      c={
                        isExpired
                          ? 'red'
                          : status?.is_approved
                            ? 'green'
                            : 'gray.4'
                      }
                    >
                      {isExpired
                        ? 'Code expired'
                        : status?.is_approved
                          ? 'Approved!'
                          : 'Waiting for approval...'}
                    </Text>
                  </Group>
                </Paper>

                <Text size="sm" c="gray.6">
                  <Text span fw={600} c="gray.5">
                    Expires:
                  </Text>{' '}
                  {new Date(deviceCode.expires_at).toLocaleString()}
                </Text>
              </Stack>

              {/* OR divider with vertical lines */}
              <div className={styles.orDivider}>
                <div className={styles.orLine} />
                <Text size="lg" fw={600} c="gray.5">
                  OR
                </Text>
                <div className={styles.orLine} />
              </div>

              {/* Right side - QR code */}
              <Stack align="center" gap="md" className={styles.rightSection}>
                <Text size="lg" fw={600} c="gray.4">
                  Scan QR to approve
                </Text>

                {qrDataUrl ? (
                  <div className={styles.qrWrapper}>
                    <img
                      src={qrDataUrl}
                      alt="QR Code"
                      className={styles.qrImage}
                    />
                  </div>
                ) : (
                  <div className={styles.qrPlaceholder}>
                    <Loader size="lg" color="cyan" />
                  </div>
                )}
              </Stack>
            </Group>
          )
        )}

        {error && (
          <Alert color="red" className={styles.errorAlert}>
            {error}
          </Alert>
        )}
      </div>
    </div>
  )
}
